import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SessionManagerService {
  private readonly SESSION_KEY = 'sc_unique_session_id';

  constructor() {
    this.initializeSession();
  }

  /**
   * Initializes a unique session ID if one does not already exist.
   */
  private initializeSession(): void {
    if (!sessionStorage.getItem(this.SESSION_KEY)) {
      const newSessionId = this.generateUUID();
      sessionStorage.setItem(this.SESSION_KEY, newSessionId);
    }
  }

  /**
   * Retrieves the current unique session ID.
   */
  public getSessionId(): string {
    let sessionId = sessionStorage.getItem(this.SESSION_KEY);
    
    // Defensive check: If sessionStorage was programmatically cleared, regenerate it.
    if (!sessionId) {
      this.initializeSession();
      sessionId = sessionStorage.getItem(this.SESSION_KEY) || '';
    }
    
    return sessionId;
  }

  /**
   * Generates a cryptographically secure UUID (Universally Unique Identifier)
   */
  private generateUUID(): string {
    // Native modern browser support
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback math-based generator for legacy/testing environments
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.trunc(Math.random() * 16); 
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Clears the unique session ID context. 
   * Call this during logout so that the next user (or next login) gets a completely fresh session ID.
   */
  clearSession() {
    sessionStorage.removeItem(this.SESSION_KEY);
  }
}